import { useState, useMemo, useCallback, useLayoutEffect, useEffect, useRef, startTransition } from "react";
import { TextField, Select, MenuItem, InputAdornment, IconButton } from "@mui/material";
import { postSquareOff, postAddMore } from "../../api/brokerBooks";
import { formatApiMessage } from "../../utils/formatApiMessage";
import { orderStatusNavigationStateFromResult, openOrderStatusInNewTab } from "../../utils/normalizeOrderStatusResponse";
import {
  normalizePositiveQtyNumeric,
  clampDisclosedPct,
  mergeDisclosedPercentIntoRows,
  computeRowDisclosedQuantity,
  initialModalDisclosedQuantity,
  normalizeDisclosedQuantityForApi,
} from "../../utils/squareOffDisclosedQuantity";
import { QUANTITY_SPINNER_DOWN, QUANTITY_SPINNER_UP } from "../QuantitySpinnerIcons/QuantitySpinnerIcons";
import BookAddMoreSqOffButtons from "../BookOrderActions/BookAddMoreSqOffButtons";
import SquareOffResultSnackbar from "../BookOrderActions/SquareOffResultSnackbar";
import MultiAccountOrderModal from "../MultiAccountOrderModal/MultiAccountOrderModal";
import modalStyles from "../MultiAccountOrderModal/MultiAccountOrderModal.module.css";
import SqOffModalAccountRow from "./SqOffModalAccountRow";

/** Bid-Ask % / limit price: center value (header + rows — `sx` beats MUI / theme on the real input). */
const BID_ASK_NUMBER_FIELD_SX = {
  "& .MuiInputBase-input": {
    textAlign: "center",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  "& .MuiOutlinedInput-input": {
    textAlign: "center",
  },
  "& input[type='number']": {
    textAlign: "center",
  },
};

/** Default header disclosed % (SQ OFF / Add More modal). */
const DEFAULT_DISCLOSED_HEADER_PCT = 10;

function flatModalIds(groupedModalData) {
  return Object.values(groupedModalData).flatMap((rows) => rows.map((r) => r.modalId));
}

/** Parsed clamped % from header string, or null if blank. */
function clampedPctFromHeaderDisclosed(headerDisclosedInput) {
  const t = String(headerDisclosedInput ?? "").trim();
  if (t === "") return null;
  return clampDisclosedPct(parseFloat(t));
}

/** Implied % from first row's qty vs disclosed (for header stepper when input empty). */
function deriveDisclosedPercentFromRow(row, modalRowSnapshot) {
  const modalId = row.modalId;
  const m = modalRowSnapshot[modalId] || {};
  const qtyRaw = m.quantity !== undefined ? m.quantity : row.quantity;
  const qty = normalizePositiveQtyNumeric(qtyRaw);
  const discRaw = m.disclosedQuantity !== undefined ? m.disclosedQuantity : row.disclosedQuantity;
  const disc = normalizePositiveQtyNumeric(discRaw);
  if (qty <= 0) return 0;
  return clampDisclosedPct((disc / qty) * 100);
}

function SpinnerControlButtons({ onIncrement, onDecrement }) {
  return (
    <div className={modalStyles["spinner-controls"]}>
      <IconButton
        type="button"
        size="small"
        onClick={onIncrement}
        className={modalStyles["spinner-button"]}
        aria-label="Increase value"
      >
        {QUANTITY_SPINNER_UP}
      </IconButton>
      <IconButton
        type="button"
        size="small"
        onClick={onDecrement}
        className={modalStyles["spinner-button"]}
        aria-label="Decrease value"
      >
        {QUANTITY_SPINNER_DOWN}
      </IconButton>
    </div>
  );
}

function OrderRowSpinnerAdornment({ onIncrement, onDecrement }) {
  return (
    <InputAdornment position="end">
      <SpinnerControlButtons onIncrement={onIncrement} onDecrement={onDecrement} />
    </InputAdornment>
  );
}

function BookSquareOffAddMoreFlow({
  bookType,
  selectedRowsData,
  selectedCount,
  buttonClassName,
  containerClassName,
  toolbarPrefix = null,
  onCtaModalOpenChange = null,
}) {
  const isHolding = bookType === "holding";
  const layoutVariant = isHolding ? "holding" : "position";

  const [isSqOffModalOpen, setIsSqOffModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("sq-off");
  const [modalRowData, setModalRowData] = useState({});
  const [orderTypeState, setOrderTypeState] = useState({ orderType: "Bid-Ask%", value: 50.0 });
  const [globalBidAskPct, setGlobalBidAskPct] = useState(50.0);
  const [headerQuantityInput, setHeaderQuantityInput] = useState("");
  const [headerDisclosedInput, setHeaderDisclosedInput] = useState("");
  const [sqOffSnackbar, setSqOffSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [sqOffSubmitting, setSqOffSubmitting] = useState(false);

  useEffect(() => {
    onCtaModalOpenChange?.(isSqOffModalOpen);
  }, [isSqOffModalOpen, onCtaModalOpenChange]);

  const openAddMore = useCallback(() => {
    if (selectedCount > 0) {
      setHeaderQuantityInput("");
      setModalMode("add-more");
      setIsSqOffModalOpen(true);
    }
  }, [selectedCount]);

  const openSqOff = useCallback(() => {
    if (selectedCount > 0) {
      setHeaderQuantityInput("");
      setModalMode("sq-off");
      setIsSqOffModalOpen(true);
    }
  }, [selectedCount]);

  const closeModal = useCallback(() => {
    setIsSqOffModalOpen(false);
    setModalMode("sq-off");
    setModalRowData({});
    setHeaderQuantityInput("");
    setHeaderDisclosedInput("");
    setOrderTypeState({ orderType: "Bid-Ask%", value: 50.0 });
    setGlobalBidAskPct(50.0);
  }, []);

  const bidAskSelectMenuProps = useMemo(
    () => ({
      PaperProps: { className: modalStyles["modal-bidask-menu-paper"] },
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
    }),
    []
  );

  const groupedModalData = useMemo(() => {
    if (!selectedRowsData?.length) return {};
    const grouped = {};
    selectedRowsData.forEach((row, index) => {
      const symbol = row.exchange_trading_symbol || "Unknown";
      if (!grouped[symbol]) grouped[symbol] = [];
      const qtyBase = normalizePositiveQtyNumeric(isHolding ? row.total_quantity || 0 : row.net_quantity || 0);
      grouped[symbol].push({
        ...row,
        modalId: row.key || `${symbol}_${row.broker_client_code}_${index}`,
        quantity: qtyBase,
        disclosedQuantity: initialModalDisclosedQuantity(isHolding, row),
        bidAskPct: 50.0,
      });
    });
    return grouped;
  }, [selectedRowsData, isHolding]);

  const groupedDataKeys = useMemo(() => Object.keys(groupedModalData), [groupedModalData]);
  const hasGroupedData = groupedDataKeys.length > 0;
  const flatRows = useMemo(() => Object.values(groupedModalData).flat(), [groupedModalData]);

  const disclosedDefaultAppliedRef = useRef(false);

  /** On open: default disclosed header to 10% and apply to all rows (once per open). */
  useLayoutEffect(() => {
    if (!isSqOffModalOpen) {
      disclosedDefaultAppliedRef.current = false;
      return;
    }
    if (!hasGroupedData || flatRows.length === 0) return;
    if (disclosedDefaultAppliedRef.current) return;
    disclosedDefaultAppliedRef.current = true;
    setHeaderDisclosedInput(String(DEFAULT_DISCLOSED_HEADER_PCT));
    setModalRowData((prev) =>
      mergeDisclosedPercentIntoRows(prev, flatRows, DEFAULT_DISCLOSED_HEADER_PCT)
    );
  }, [isSqOffModalOpen, hasGroupedData, flatRows]);

  const handleModalRowDataChange = useCallback(
    (modalId, field, value) => {
      let stored = value;
      if (field === "quantity" || field === "disclosedQuantity") {
        const trimmed = String(value).trim();
        if (trimmed === "") {
          stored = "";
        } else {
          const n = parseFloat(trimmed);
          if (!Number.isNaN(n) && n < 0) {
            stored = Math.abs(n);
          }
        }
      }
      setModalRowData((prev) => {
        const currentValue = prev[modalId]?.[field];
        if (currentValue === stored) return prev;
        const next = {
          ...prev,
          [modalId]: {
            ...prev[modalId],
            [field]: stored,
          },
        };
        if (field === "quantity") {
          const pct = clampedPctFromHeaderDisclosed(headerDisclosedInput);
          if (pct !== null) {
            const row = flatRows.find((r) => r.modalId === modalId);
            if (row) {
              const qtyRaw =
                next[modalId]?.quantity !== undefined ? next[modalId].quantity : row.quantity;
              const qty = normalizePositiveQtyNumeric(qtyRaw);
              next[modalId] = {
                ...next[modalId],
                disclosedQuantity: computeRowDisclosedQuantity(row.exchange, qty, pct),
              };
            }
          }
        }
        return next;
      });
    },
    [headerDisclosedInput, flatRows]
  );

  const getModalRowValue = useCallback(
    (row, field) => {
      const modalId = row.modalId;
      const stateValue = modalRowData[modalId]?.[field];
      if (stateValue !== undefined) return stateValue;
      if (isHolding) {
        if (field === "bidAskPct") return orderTypeState.value;
        if (field === "orderType") return orderTypeState.orderType;
      } else {
        if (field === "bidAskPct") return globalBidAskPct;
        if (field === "orderType") return orderTypeState.orderType;
      }
      return row[field] ?? "";
    },
    [modalRowData, isHolding, orderTypeState, globalBidAskPct]
  );

  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;
  const getModalRowValueRef = useRef(getModalRowValue);
  getModalRowValueRef.current = getModalRowValue;
  const handleModalRowDataChangeRef = useRef(handleModalRowDataChange);
  handleModalRowDataChangeRef.current = handleModalRowDataChange;

  /** Stable identity so memoized rows do not re-render when only another row updates. */
  const bumpQuantity = useCallback((modalId, delta) => {
    const row = flatRowsRef.current.find((r) => r.modalId === modalId);
    if (!row) return;
    const q = normalizePositiveQtyNumeric(getModalRowValueRef.current(row, "quantity"));
    const next = delta > 0 ? q + delta : Math.max(0, q + delta);
    handleModalRowDataChangeRef.current(modalId, "quantity", next);
  }, []);

  const bumpDisclosed = useCallback((modalId, delta) => {
    const row = flatRowsRef.current.find((r) => r.modalId === modalId);
    if (!row) return;
    const d = normalizePositiveQtyNumeric(getModalRowValueRef.current(row, "disclosedQuantity"));
    const next = delta > 0 ? d + delta : Math.max(0, d + delta);
    handleModalRowDataChangeRef.current(modalId, "disclosedQuantity", next);
  }, []);

  const applyQuantityToAll = useCallback(
    (rawValue) => {
      const trimmed = String(rawValue).trim();
      if (trimmed === "") {
        setHeaderQuantityInput("");
        startTransition(() => {
          setModalRowData((prev) => {
            const allModalIds = flatModalIds(groupedModalData);
            const next = { ...prev };
            allModalIds.forEach((modalId) => {
              if (!next[modalId]) return;
              const { quantity, ...rest } = next[modalId];
              if (Object.keys(rest).length === 0) delete next[modalId];
              else next[modalId] = rest;
            });
            const pct = clampedPctFromHeaderDisclosed(headerDisclosedInput);
            if (pct !== null) return mergeDisclosedPercentIntoRows(next, flatRows, pct);
            return next;
          });
        });
        return;
      }
      const num = parseFloat(trimmed);
      const numOrZero = Number.isNaN(num) ? 0 : normalizePositiveQtyNumeric(num);
      setHeaderQuantityInput(String(numOrZero));
      startTransition(() => {
        setModalRowData((prev) => {
          const allModalIds = flatModalIds(groupedModalData);
          let updated = { ...prev };
          allModalIds.forEach((modalId) => {
            updated[modalId] = { ...(updated[modalId] || {}), quantity: numOrZero };
          });
          const pct = clampedPctFromHeaderDisclosed(headerDisclosedInput);
          if (pct !== null) updated = mergeDisclosedPercentIntoRows(updated, flatRows, pct);
          return updated;
        });
      });
    },
    [groupedModalData, flatRows, headerDisclosedInput]
  );

  const applyDisclosedToAll = useCallback(
    (rawValue) => {
      const trimmed = String(rawValue).trim();
      if (trimmed === "") {
        setHeaderDisclosedInput("");
        startTransition(() => {
          setModalRowData((prev) => {
            const allModalIds = flatModalIds(groupedModalData);
            const next = { ...prev };
            allModalIds.forEach((modalId) => {
              if (!next[modalId]) return;
              const { disclosedQuantity, ...rest } = next[modalId];
              if (Object.keys(rest).length === 0) delete next[modalId];
              else next[modalId] = rest;
            });
            return next;
          });
        });
        return;
      }
      const pctNum = clampDisclosedPct(parseFloat(trimmed));
      setHeaderDisclosedInput(String(pctNum));
      startTransition(() => {
        setModalRowData((prev) => mergeDisclosedPercentIntoRows(prev, flatRows, pctNum));
      });
    },
    [groupedModalData, flatRows]
  );

  const handleHeaderQuantityStep = useCallback(
    (delta) => {
      const firstRow = flatRows[0];
      if (!firstRow) return;
      const baseRaw =
        headerQuantityInput !== ""
          ? parseFloat(headerQuantityInput) || 0
          : parseFloat(getModalRowValue(firstRow, "quantity")) || 0;
      const base = normalizePositiveQtyNumeric(baseRaw);
      const next = Math.max(0, base + delta);
      applyQuantityToAll(String(next));
    },
    [flatRows, headerQuantityInput, getModalRowValue, applyQuantityToAll]
  );

  const handleHeaderDisclosedStep = useCallback(
    (delta) => {
      const firstRow = flatRows[0];
      if (!firstRow) return;
      const base =
        headerDisclosedInput !== ""
          ? clampDisclosedPct(parseFloat(headerDisclosedInput) || 0)
          : deriveDisclosedPercentFromRow(firstRow, modalRowData);
      const next = clampDisclosedPct(base + delta);
      applyDisclosedToAll(String(next));
    },
    [flatRows, headerDisclosedInput, modalRowData, applyDisclosedToAll]
  );

  const handleHeaderOrderTypeChange = useCallback((orderType) => {
    setOrderTypeState((prev) => ({ ...prev, orderType }));
  }, []);

  const handleHeaderValueChange = useCallback(
    (value) => {
      const num = parseFloat(value) || 0;
      setOrderTypeState((prev) => ({ ...prev, value: num }));
      setModalRowData((prev) => {
        const allModalIds = flatModalIds(groupedModalData);
        const updated = { ...prev };
        let hasChanges = false;
        allModalIds.forEach((modalId) => {
          if (updated[modalId]?.bidAskPct !== num) {
            updated[modalId] = { ...updated[modalId], bidAskPct: num };
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    },
    [groupedModalData]
  );

  const handleGlobalBidAskPctChange = useCallback(
    (value) => {
      setGlobalBidAskPct(value);
      setModalRowData((prev) => {
        const allModalIds = flatModalIds(groupedModalData);
        const updated = { ...prev };
        let hasChanges = false;
        allModalIds.forEach((modalId) => {
          if (updated[modalId]?.bidAskPct !== value) {
            updated[modalId] = { ...updated[modalId], bidAskPct: value };
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    },
    [groupedModalData]
  );

  const handleConfirmSqOff = useCallback(async () => {
    const allRows = Object.values(groupedModalData).flat();

    let order_list;
    let book_name;

    if (isHolding) {
      const finalData = allRows.map((row) => ({
        ...row,
        ...(modalRowData[row.modalId] || {}),
        orderType: modalRowData[row.modalId]?.orderType ?? orderTypeState.orderType,
        bidAskPct: modalRowData[row.modalId]?.bidAskPct ?? orderTypeState.value,
      }));
      order_list = finalData.map((row) => ({
        broker_client_code: row.broker_client_code ?? "",
        exchange: row.exchange ?? "NSE",
        exchange_trading_symbol: row.exchange_trading_symbol ?? "",
        eq_isin: row.eq_isin ?? "",
        order_quantity: normalizePositiveQtyNumeric(row.quantity),
        disclosed_quantity: normalizeDisclosedQuantityForApi(row.exchange, row.disclosedQuantity),
        order_price: Number(row.bidAskPct) || 0,
        order_type: row.orderType === "LIMIT" ? "LIMIT" : "BID_ASK",
      }));
      book_name = "holding";
    } else {
      const finalData = allRows.map((row) => ({
        ...row,
        ...(modalRowData[row.modalId] || {}),
        orderType: modalRowData[row.modalId]?.orderType ?? orderTypeState.orderType,
        bidAskPct: modalRowData[row.modalId]?.bidAskPct ?? globalBidAskPct,
      }));
      order_list = finalData.map((row) => {
        const quantity = normalizePositiveQtyNumeric(row.quantity);
        const disclosedQuantity = normalizeDisclosedQuantityForApi(row.exchange, row.disclosedQuantity);
        const orderPrice = Number(row.bidAskPct) || 0;
        const exchangeToken =
          row.exchange_token ||
          (row.key && row.key.split("_").length >= 3 ? row.key.split("_")[2] : "");
        return {
          broker_client_code: row.broker_client_code ?? "",
          exchange: row.exchange ?? "NSE",
          exchange_token: exchangeToken,
          product_type: row.product_type ?? "DELIVERY",
          order_quantity: quantity,
          disclosed_quantity: disclosedQuantity,
          order_price: orderPrice,
          order_type: row.orderType === "LIMIT" ? "LIMIT" : "BID_ASK",
        };
      });
      book_name = "position";
    }

    const payload = { order_list, book_name };
    const isAddMore = modalMode === "add-more";
    const postApi = isAddMore ? postAddMore : postSquareOff;
    const actionLabel = isAddMore ? "Add more" : "Square off";

    setSqOffSubmitting(true);
    try {
      const result = await postApi(payload);
      const hasErrors = result?.errors?.length > 0;
      const hasSuccess = result?.placement_response?.length > 0;
      const hasRejectedPlacement = (result?.placement_response ?? []).some((r) => {
        const s = String(r.status ?? "").toUpperCase();
        return (
          s === "REJECTED" ||
          s === "FAILED" ||
          s === "FAILURE" ||
          s === "ORDER NOT PLACED"
        );
      });

      setIsSqOffModalOpen(false);
      setModalMode("sq-off");
      setModalRowData({});

      const errLine = (e) =>
        formatApiMessage(e?.error) ?? formatApiMessage(e?.message) ?? "Unknown error";

      if (hasErrors && !hasSuccess) {
        const msg = result.errors.map(errLine).join("; ");
        setSqOffSnackbar({ open: true, message: msg, severity: "error" });
      } else if (hasErrors) {
        const msg = `Partial: ${result.errors.length} failed. ${result.errors.map(errLine).join("; ")}`;
        setSqOffSnackbar({ open: true, message: msg, severity: "warning" });
      } else if (hasRejectedPlacement) {
        setSqOffSnackbar({
          open: true,
          message: "One or more orders were rejected. See order status for details.",
          severity: "warning",
        });
      } else {
        setSqOffSnackbar({ open: true, message: `${actionLabel} placed successfully.`, severity: "success" });
      }
      openOrderStatusInNewTab(orderStatusNavigationStateFromResult(result));
    } catch (err) {
      const msg =
        formatApiMessage(err?.response?.data?.detail) ??
        formatApiMessage(err?.response?.data?.message) ??
        (typeof err?.message === "string" && err.message.trim() !== "" ? err.message : null) ??
        `${actionLabel} failed.`;
      setIsSqOffModalOpen(false);
      setModalMode("sq-off");
      setModalRowData({});
      setSqOffSnackbar({ open: true, message: msg, severity: "error" });
      openOrderStatusInNewTab({ squareOffError: msg });
    } finally {
      setSqOffSubmitting(false);
    }
  }, [groupedModalData, modalRowData, orderTypeState, globalBidAskPct, modalMode, isHolding]);

  const sqOffModalBody = !hasGroupedData ? (
    <div className={modalStyles["modal-empty"]}>No data available</div>
  ) : (
    groupedDataKeys.map((symbol) => {
      const rows = groupedModalData[symbol];
      return (
        <div key={symbol} className={modalStyles["modal-group-container"]}>
          <div className={modalStyles["modal-group"]}>
            <div className={modalStyles["modal-symbol-cell"]}>
              <span className={modalStyles["modal-symbol-label"]}>{symbol}</span>
            </div>
            <div className={modalStyles["modal-rows-container"]}>
              {rows.map((row) => (
                <SqOffModalAccountRow
                  key={row.modalId}
                  modalId={row.modalId}
                  brokerLabel={row.broker_client_code || "-"}
                  quantity={getModalRowValue(row, "quantity")}
                  disclosedQuantity={getModalRowValue(row, "disclosedQuantity")}
                  rowExchange={row.exchange}
                  bidAskPct={getModalRowValue(row, "bidAskPct")}
                  orderType={getModalRowValue(row, "orderType")}
                  bidAskSelectMenuProps={bidAskSelectMenuProps}
                  onFieldChange={handleModalRowDataChange}
                  bumpQuantity={bumpQuantity}
                  bumpDisclosed={bumpDisclosed}
                />
              ))}
            </div>
          </div>
        </div>
      );
    })
  );

  const headerQuantity = useMemo(
    () => (
      <div className={modalStyles["modal-header-input-wrap"]}>
        <TextField
          size="small"
          type="number"
          value={headerQuantityInput}
          placeholder="Apply to all"
          onChange={(e) => applyQuantityToAll(e.target.value)}
          disabled={sqOffSubmitting}
          className={modalStyles["modal-input"]}
          inputProps={{ min: 0, step: 1 }}
          InputProps={{
            endAdornment: (
              <OrderRowSpinnerAdornment
                onIncrement={() => handleHeaderQuantityStep(1)}
                onDecrement={() => handleHeaderQuantityStep(-1)}
              />
            ),
          }}
        />
      </div>
    ),
    [headerQuantityInput, applyQuantityToAll, handleHeaderQuantityStep, sqOffSubmitting]
  );

  const headerDisclosed = useMemo(
    () => (
      <div className={modalStyles["modal-header-input-wrap"]}>
        <TextField
          size="small"
          type="number"
          value={headerDisclosedInput}
          placeholder="% of each qty"
          title="Percentage of each row's quantity (e.g. 10 = 10% of that row's quantity)"
          onChange={(e) => applyDisclosedToAll(e.target.value)}
          disabled={sqOffSubmitting}
          className={modalStyles["modal-input"]}
          inputProps={{ min: 0, max: 100, step: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" className={modalStyles["modal-disclosed-pct-adornment"]}>
                <span className={modalStyles["modal-disclosed-pct-suffix"]} aria-hidden>
                  %
                </span>
                <SpinnerControlButtons
                  onIncrement={() => handleHeaderDisclosedStep(1)}
                  onDecrement={() => handleHeaderDisclosedStep(-1)}
                />
              </InputAdornment>
            ),
          }}
        />
      </div>
    ),
    [headerDisclosedInput, applyDisclosedToAll, handleHeaderDisclosedStep, sqOffSubmitting]
  );

  const headerBidAsk = useMemo(
    () =>
      isHolding ? (
        <div className={modalStyles["modal-bidask-container"]}>
          <Select
            value={orderTypeState.orderType}
            size="small"
            className={modalStyles["modal-bidask-select"]}
            disabled={sqOffSubmitting}
            onChange={(e) => handleHeaderOrderTypeChange(e.target.value)}
            MenuProps={bidAskSelectMenuProps}
          >
            <MenuItem value="Bid-Ask%">Bid-Ask%</MenuItem>
            <MenuItem value="LIMIT">Limit order</MenuItem>
          </Select>
          <TextField
            size="small"
            type="number"
            value={orderTypeState.value}
            disabled={sqOffSubmitting}
            onChange={(e) => handleHeaderValueChange(e.target.value)}
            className={modalStyles["modal-bidask-number"]}
            sx={BID_ASK_NUMBER_FIELD_SX}
          />
        </div>
      ) : (
        <div className={modalStyles["modal-bidask-container"]}>
          <Select
            value={orderTypeState.orderType}
            size="small"
            className={modalStyles["modal-bidask-select"]}
            disabled={sqOffSubmitting}
            onChange={(e) => handleHeaderOrderTypeChange(e.target.value)}
            MenuProps={bidAskSelectMenuProps}
          >
            <MenuItem value="Bid-Ask%">Bid-Ask%</MenuItem>
            <MenuItem value="LIMIT">Limit order</MenuItem>
          </Select>
          <TextField
            size="small"
            type="number"
            value={globalBidAskPct}
            disabled={sqOffSubmitting}
            onChange={(e) => handleGlobalBidAskPctChange(parseFloat(e.target.value) || 0)}
            className={modalStyles["modal-bidask-number"]}
            sx={BID_ASK_NUMBER_FIELD_SX}
          />
        </div>
      ),
    [
      isHolding,
      orderTypeState.orderType,
      orderTypeState.value,
      globalBidAskPct,
      handleHeaderOrderTypeChange,
      handleHeaderValueChange,
      handleGlobalBidAskPctChange,
      bidAskSelectMenuProps,
      sqOffSubmitting,
    ]
  );

  const addMoreDisabled = selectedCount === 0;
  const sqOffDisabled = selectedCount === 0;

  return (
    <>
      <div className={containerClassName}>
        {toolbarPrefix}
        <BookAddMoreSqOffButtons
          buttonClassName={buttonClassName}
          onAddMore={openAddMore}
          onSqOff={openSqOff}
          addMoreDisabled={addMoreDisabled}
          sqOffDisabled={sqOffDisabled}
        />
      </div>

      <MultiAccountOrderModal
        open={isSqOffModalOpen}
        onClose={closeModal}
        onConfirm={handleConfirmSqOff}
        modalMode={modalMode}
        layoutVariant={layoutVariant}
        headerQuantity={headerQuantity}
        headerDisclosed={headerDisclosed}
        headerBidAsk={headerBidAsk}
        title="Multi Account Order Entry"
        submitting={sqOffSubmitting}
      >
        {sqOffModalBody}
      </MultiAccountOrderModal>

      <SquareOffResultSnackbar
        state={sqOffSnackbar}
        onClose={() => setSqOffSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

export default BookSquareOffAddMoreFlow;
